/**
 * 问真主流口径神煞引擎（规则驱动，算法镜像后端 WenzhenShenshaEngine）
 * 规则数据：shenshaRules.ts（转录自后端 wenzhen-shensha.json，78 条）
 *
 * 命中位置 -> 柱索引：1,7,13,23->0；2,8,14,24->1；3,9,15,25->2；4,10,16,26->3；
 * 17 年纳音、18 天干串仅作源；-1 表示天干整体（仅三奇贵人）。
 */

import { SHENSHA_RULES } from './shenshaRules';

const PILLAR_INDEX_OF: Record<number, number> = {
  1: 0, 7: 0, 13: 0, 23: 0,
  2: 1, 8: 1, 14: 1, 24: 1,
  3: 2, 9: 2, 15: 2, 25: 2,
  4: 3, 10: 3, 16: 3, 26: 3,
};

/** 年柱干支 -> 纳音五行（60 甲子纳音末字） */
const NAYIN: Record<string, string> = {
  甲子: '金', 乙丑: '金', 丙寅: '火', 丁卯: '火', 戊辰: '木', 己巳: '木',
  庚午: '土', 辛未: '土', 壬申: '金', 癸酉: '金', 甲戌: '火', 乙亥: '火',
  丙子: '水', 丁丑: '水', 戊寅: '土', 己卯: '土', 庚辰: '金', 辛巳: '金',
  壬午: '木', 癸未: '木', 甲申: '水', 乙酉: '水', 丙戌: '土', 丁亥: '土',
  戊子: '火', 己丑: '火', 庚寅: '木', 辛卯: '木', 壬辰: '水', 癸巳: '水',
  甲午: '金', 乙未: '金', 丙申: '火', 丁酉: '火', 戊戌: '木', 己亥: '木',
  庚子: '土', 辛丑: '土', 壬寅: '金', 癸卯: '金', 甲辰: '火', 乙巳: '火',
  丙午: '水', 丁未: '水', 戊申: '土', 己酉: '土', 庚戌: '金', 辛亥: '金',
  壬子: '木', 癸丑: '木', 甲寅: '水', 乙卯: '水', 丙辰: '土', 丁巳: '土',
  戊午: '火', 己未: '火', 庚申: '木', 辛酉: '木', 壬戌: '水', 癸亥: '水',
};

export interface ShenshaHitRaw {
  name: string;
  /** 0年1月2日3时，-1 天干整体（三奇贵人） */
  pillarIndexes: number[];
}

/**
 * 计算神煞命中（镜像后端 WenzhenShenshaEngine.calc）。
 * @param pillars 四柱干支，顺序 年/月/日/时（如 "甲子"），长度 4
 * @param gender  1 男 / 2 女（仅元辰区分）
 * @return 按问真名单 order 稳定排序后的命中列表
 */
export function calcShenshaHits(
  pillars: [string, string, string, string],
  gender: 1 | 2 = 1,
): ShenshaHitRaw[] {
  const pos: Record<number, string> = {};
  pillars.forEach((gz, i) => {
    pos[1 + i] = gz[0];
    pos[7 + i] = gz[1];
    pos[13 + i] = gz;
    pos[23 + i] = gz;
  });
  pos[17] = NAYIN[pillars[0]] ?? '';
  pos[18] = pillars.map((p) => p[0]).join('');

  const merged = new Map<string, number[]>();
  const orderMap = new Map<string, number>();
  const add = (name: string, idx: number) => {
    const list = merged.get(name) ?? [];
    if (!list.includes(idx)) list.push(idx);
    merged.set(name, list);
  };

  for (const rule of SHENSHA_RULES) {
    if (rule.sex !== 0 && rule.sex !== gender) continue;
    orderMap.set(rule.name, rule.order);
    if (rule.name === '三奇贵人') {
      const stems = pos[18];
      for (const key of Object.keys(rule.data)) {
        if (stems.includes(key)) {
          add(rule.name, -1);
          break;
        }
      }
      continue;
    }
    for (const srcPos of rule.main) {
      const srcVal = pos[srcPos];
      if (srcVal == null) continue;
      const targets = rule.data[srcVal];
      if (targets == null) continue;
      if (targets.length === 0) {
        add(rule.name, PILLAR_INDEX_OF[srcPos] ?? -1);
      } else {
        for (const t of targets) {
          for (const tagPos of rule.tags) {
            const tagVal = pos[tagPos];
            if (tagVal != null && tagVal === t) add(rule.name, PILLAR_INDEX_OF[tagPos] ?? -1);
          }
        }
      }
    }
  }
  return [...merged.entries()]
    .map(([name, pillarIndexes]) => ({ name, pillarIndexes }))
    .sort((a, b) => (orderMap.get(a.name) ?? 0) - (orderMap.get(b.name) ?? 0));
}